import { useTimeZone } from '@/auth/useTimeZone.js';
import { SessionFeedbackFormSchema } from '@/components/feedbacks/session-feedback-utils.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { InfoCard } from '@/components/info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { getAdminSession, getCoach } from '@/lib/api-calls.js';
import { formatInstant, InstantCompare } from '@airreading/common';
import { SessionFeedbackWithAssessment } from '@airreading/common/data';
import { useSuspenseQueries } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';

interface FeedbackSubmissionDetailsProps {
  sessionFeedback: SessionFeedbackWithAssessment;
  form: UseFormReturn<SessionFeedbackFormSchema>;
  editing: boolean;
  valueTestId?: (field: string) => string;
}

export const FeedbackSubmissionDetails = ({
  sessionFeedback,
  form,
  editing,
  valueTestId,
}: FeedbackSubmissionDetailsProps) => {
  const { coachId, submittedAtInstant, lateFeedbackDismissed } = sessionFeedback;
  const [{ data: coach }, { data: session }] = useSuspenseQueries({
    queries: [getCoach(coachId), getAdminSession(sessionFeedback.sessionId)],
  });
  const { timeZone } = useTimeZone();
  const { endInstant } = session;
  const submittedOver24h =
    submittedAtInstant && InstantCompare(submittedAtInstant).isAfter(endInstant.add({ hours: 24 }));
  const feedbackStatusLabel = (() => {
    if (!submittedAtInstant) return 'Not sent';
    if (!submittedOver24h || lateFeedbackDismissed) return 'Sent';
    return 'Sent (>24 H)';
  })();

  return (
    <InfoCard headerText="Teacher submission information">
      <InfoKeyValue
        label={'Teacher'}
        value={<span data-testid={valueTestId?.('teacher')}>{coach.fullNameOrEmail}</span>}
      />
      <InfoKeyValue
        label={'Teacher overall grade'}
        value={<span data-testid={valueTestId?.('teacher-overall-grade')}>{coach.overallGrade ?? '-'}</span>}
      />
      <InfoKeyValue
        label={'Feedback status'}
        value={<span data-testid={valueTestId?.('feedback-status')}>{feedbackStatusLabel}</span>}
      />
      <InfoKeyValue
        label={'Feedback sent time'}
        value={
          <span data-testid={valueTestId?.('feedback-sent-time')}>
            {submittedAtInstant
              ? `${formatInstant(submittedAtInstant, timeZone, { hour12: true }).formattedDateTime} (${timeZone})`
              : 'N/A'}
          </span>
        }
      />
      <InfoKeyValue
        label={'Dismiss late teacher feedback'}
        value={
          <span data-testid={valueTestId?.('dismiss-late-feedback')}>
            {lateFeedbackDismissed == null ? 'N/A' : lateFeedbackDismissed ? 'Yes' : 'No'}
          </span>
        }
        edit={{
          editing,
          editModeComponent: (
            <FormCheckbox name="lateFeedbackDismissed" label="Dismiss late teacher feedback" form={form} />
          ),
        }}
      />
    </InfoCard>
  );
};
